import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Timer } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { reservationsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Reservation, ReservationStatus } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { ReservationStatusBadge } from './shared';

const STATUSES: { value: ReservationStatus; label: string }[] = [
  { value: 'PENDING', label: 'Waiting in the queue' },
  { value: 'READY', label: 'Ready to collect' },
  { value: 'COLLECTED', label: 'Collected' },
  { value: 'EXPIRED', label: 'Lapsed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/**
 * The reservation queues.
 *
 * <p>The position column is the queue as the server keeps it, not as this page
 * works it out: the ordering is decided under a lock, and a browser sorting rows
 * by request time would occasionally disagree with what a member was told.
 *
 * <p>Expiring lapsed holds is a button rather than a nightly job, matching every
 * other sweep in this system. There is no scheduler, and a job nobody watches is
 * worse than a button somebody presses.
 */
export function ReservationsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('requestedAt,desc');
  const manages = can(PERMISSIONS.RESERVATION_MANAGE);

  const [status, setStatus] = useState<ReservationStatus | ''>('');

  const params = { ...table.params, ...(status === '' ? {} : { status: [status] }) };

  const reservations = useQuery({
    queryKey: queryKeys.reservations.list(params),
    queryFn: () => reservationsApi.list(params),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
  };

  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      reservationsApi.cancel(id, reason),
    onSuccess: (row) => {
      toast.success(`${row.reservationNumber} cancelled`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const expire = useMutation({
    mutationFn: () => reservationsApi.expireLapsed(),
    onSuccess: (count) => {
      toast.success(
        count === 0
          ? 'Nothing had lapsed'
          : `${count} reservation${count === 1 ? '' : 's'} expired; any held copies were offered on`,
      );
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Reservation>[] = [
    {
      key: 'position',
      header: '#',
      className: 'text-right',
      render: (row) =>
        row.status === 'PENDING' ? (
          <span className="font-bold tabular-nums text-ink">{row.queuePosition}</span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      key: 'book',
      header: 'Title',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.bookTitle}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.reservationNumber}</span>
        </span>
      ),
    },
    {
      key: 'member',
      header: 'Reader',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.memberName}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.membershipNumber}</span>
        </span>
      ),
    },
    {
      key: 'requestedAt',
      header: 'Asked',
      sortKey: 'requestedAt',
      render: (row) => formatDate(row.requestedAt),
    },
    {
      key: 'ready',
      header: 'Held',
      render: (row) =>
        row.status === 'READY' ? (
          <span>
            <span className="block font-mono text-[12px] text-ink">
              {orDash(row.readyAccessionNumber)}
            </span>
            <span className="block text-[11px] text-subtle">
              collect by {row.collectBy === undefined ? '—' : formatDate(row.collectBy)}
            </span>
          </span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <ReservationStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages && (row.status === 'PENDING' || row.status === 'READY') ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const reason = window.prompt('Why is the reservation being cancelled?');
              if (reason !== null && reason.trim() !== '') {
                cancel.mutate({ id: row.id, reason: reason.trim() });
              }
            }}
          >
            Cancel
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      title="Reservations"
      description="Who is waiting for what, in the order they asked."
      icon={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
      action={
        manages ? (
          <Button
            variant="secondary"
            onClick={() => expire.mutate()}
            disabled={expire.isPending}
          >
            <Timer className="h-4 w-4" aria-hidden="true" />
            Expire what has lapsed
          </Button>
        ) : undefined
      }
      filters={
        <FilterSelect
          label="Status"
          value={status}
          onChange={(value) => {
            setStatus(value);
            table.setPage(0);
          }}
          options={STATUSES}
        />
      }
      pagination={reservations.data?.pagination}
      onPageChange={table.setPage}
      onSizeChange={table.setSize}
    >
      <DataTable
        columns={columns}
        rows={reservations.data?.data}
        rowKey={(row) => row.id}
        loading={reservations.isPending}
        error={reservations.error}
        onRetry={() => void reservations.refetch()}
        sort={table.sort}
        onSort={table.toggleSort}
        emptyTitle="Nobody is waiting"
        emptyDescription="A reader can reserve a title once every copy of it is out."
      />
    </ListPage>
  );
}
